import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import {
    Table, Input, Button, Tag, Space, notification, Select,
    Tooltip, Typography, Row, Col,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, ReloadOutlined, ExportOutlined,
    EditOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getVariables } from '../../../api/payments';
import { VariableEditorModal } from './VariableEditorModal';
import { VARIABLE_ORIGIN_OPTIONS } from '../common/constants';
import styles from '../common/styles.module.scss';

const { Text } = Typography;

interface VariableItem {
  _id: string;
  name: string;
  type: string;
  value: string;
  origin: string;
  planID?: string;
  keyName: string;
  createdAt: string;
  updatedAt: string;
}

/* ─── Helpers: tipo de variable ─── */

type VarType = 'number' | 'lookup' | 'reference' | 'conditional' | 'expression' | 'text';

const detectVarType = (value: any): VarType => {
    const strVal = String(value || '');
    if (!strVal) return 'text';
    if (!Number.isNaN(Number(strVal)) && strVal.length < 20) return 'number';
    if (strVal.startsWith('{') && strVal.includes(':')) {
        try { JSON.parse(strVal); return 'lookup'; } catch { /* fall */ }
    }
    if (/^\{\{booking\.[^}]+\}\}$/.test(strVal.trim())) return 'reference';
    if (strVal.includes('{{booking.') || strVal.includes('===') || strVal.includes('!==')) return 'conditional';
    if (strVal.startsWith('[') || strVal.includes('.reduce') || strVal.includes('.find') || strVal.includes('.filter')) return 'expression';
    return 'text';
};

const VAR_TYPE_CONFIG: Record<VarType, { label: string; color: string }> = {
    number: { label: 'Valor fijo', color: 'green' },
    lookup: { label: 'Tabla de búsqueda', color: 'gold' },
    reference: { label: 'Referencia', color: 'cyan' },
    conditional: { label: 'Condicional', color: 'blue' },
    expression: { label: 'Expresión', color: 'purple' },
    text: { label: 'Texto', color: 'default' },
};

/* ─── Component ─── */

const VariablesTab: React.FC = () => {
    const [variables, setVariables] = useState<VariableItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const searchTimer = useRef<ReturnType<typeof setTimeout>>();
    const [originFilter, setOriginFilter] = useState<string | undefined>(undefined);
    const [scopeFilter, setScopeFilter] = useState<string | undefined>(undefined);
    const [paginator, setPaginator] = useState({ page: 1, limit: 10 });
    const [total, setTotal] = useState(0);
    const [expandedVar, setExpandedVar] = useState<string | null>(null);
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingVariable, setEditingVariable] = useState<any>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: paginator.page,
                limit: paginator.limit,
            };
            if (search) params.search = search;
            if (originFilter) params.origin = originFilter;

            const { data } = await getVariables(params);
            setVariables(data.data?.data || data.data || []);
            setTotal(data.data?.total || data.total || 0);
        } catch (err) {
            notification.error({ message: 'Error loading variables' });
        } finally {
            setLoading(false);
        }
    }, [paginator, search, originFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = (value: string) => {
        setSearchInput(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setSearch(value);
            setPaginator((prev) => ({ ...prev, page: 1 }));
        }, 400);
    };

    const handleEdit = (record: VariableItem) => {
        setEditingVariable(record);
        setEditorVisible(true);
    };

    const handleCreate = () => {
        setEditingVariable({});
        setEditorVisible(true);
    };

    const handleEditorClose = (updatedVariable?: any) => {
        setEditorVisible(false);
        setEditingVariable(null);
        if (updatedVariable) {
            fetchData();
        }
    };

    const handleExport = () => {
        const csvContent = [
            ['Name', 'Key', 'Type', 'Origin', 'Is Global', 'Value', 'Created At'].join(','),
            ...variables.map((v) => [
                `"${v.name}"`,
                v.keyName,
                v.type,
                v.origin,
                v.planID ? 'No' : 'Yes',
                `"${String(v.value).replace(/"/g, '""')}"`,
                moment(v.createdAt).format('YYYY-MM-DD'),
            ].join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `variables_${moment().format('YYYY-MM-DD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filteredVariables = scopeFilter
        ? variables.filter((v) => {
            if (scopeFilter === 'global') return !v.planID;
            return !!v.planID;
        })
        : variables;

    /* ─── Render: expanded variable detail ─── */

    const renderExpandedVariable = (record: VariableItem) => {
        const strVal = String(record.value || '');
        const vType = detectVarType(record.value);

        if (vType === 'number') {
            return (
                <div style={{ padding: '8px 0' }}>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 14, display: 'block', marginBottom: 8,
                        }}
                    >
                        Esta variable tiene un valor numérico fijo que se usa directamente en la fórmula.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 20,
                        textAlign: 'center',
                    }}
                    >
                        <Text style={{
                            fontSize: 32,
                            fontWeight: 700,
                            color: '#87d068',
                            fontFamily: "'Courier New', monospace",
                        }}
                        >
                            {strVal}
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'lookup') {
            try {
                const parsed = JSON.parse(strVal);
                const isNested = Object.values(parsed).some(
                    (v) => typeof v === 'object' && v !== null,
                );
                const firstKey = Object.keys(parsed)[0] || '';
                const firstSubVal = isNested ? Object.keys(parsed[firstKey] || {})[0] || '' : '';
                const exampleVal = isNested ? parsed[firstKey]?.[firstSubVal] : parsed[firstKey];

                return (
                    <div style={{ padding: '8px 0' }}>
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 14, display: 'block', marginBottom: 12,
                            }}
                        >
                            Esta variable es una
                            {' '}
                            <strong>tabla de búsqueda</strong>
                            . Cuando se usa en una fórmula, el sistema busca el valor correspondiente
                            según las claves configuradas en la tabla.
                        </Text>
                        {isNested ? (
                            <Row gutter={[12, 12]}>
                                {Object.entries(parsed).map(([country, methods]: [string, any]) => (
                                    <Col key={country} span={12}>
                                        <div style={{
                                            background: '#141414',
                                            border: '1px solid #303030',
                                            borderRadius: 8,
                                            padding: 12,
                                            height: '100%',
                                        }}
                                        >
                                            <Text
                                                strong
                                                style={{
                                                    color: '#FAAD14', marginBottom: 8, display: 'block',
                                                }}
                                            >
                                                {country}
                                            </Text>
                                            {typeof methods === 'object' ? (
                                                <Table
                                                    size="small"
                                                    dataSource={Object.entries(methods).map(([k, v]) => ({
                                                        key: k, method: k, val: String(v),
                                                    }))}
                                                    columns={[
                                                        {
                                                            title: 'Clave',
                                                            dataIndex: 'method',
                                                            key: 'method',
                                                            render: (m: string) => <Tag>{m}</Tag>,
                                                        },
                                                        {
                                                            title: 'Valor',
                                                            dataIndex: 'val',
                                                            key: 'val',
                                                            render: (v: string) => (
                                                                <Text style={{
                                                                    color: '#87d068',
                                                                    fontFamily: "'Courier New', monospace",
                                                                }}
                                                                >
                                                                    {v}
                                                                </Text>
                                                            ),
                                                        },
                                                    ]}
                                                    pagination={false}
                                                />
                                            ) : (
                                                <Text style={{
                                                    color: '#87d068',
                                                    fontFamily: "'Courier New', monospace",
                                                }}
                                                >
                                                    {String(methods)}
                                                </Text>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <Table
                                size="small"
                                dataSource={Object.entries(parsed).map(([k, v]) => ({
                                    key: k, country: k, val: String(v),
                                }))}
                                columns={[
                                    {
                                        title: 'Clave',
                                        dataIndex: 'country',
                                        key: 'country',
                                        render: (c: string) => <Tag color="gold">{c}</Tag>,
                                    },
                                    {
                                        title: 'Valor',
                                        dataIndex: 'val',
                                        key: 'val',
                                        render: (v: string) => (
                                            <Text style={{
                                                color: '#87d068',
                                                fontFamily: "'Courier New', monospace",
                                            }}
                                            >
                                                {v}
                                            </Text>
                                        ),
                                    },
                                ]}
                                pagination={false}
                                style={{
                                    background: '#141414',
                                    borderRadius: 8,
                                    border: '1px solid #303030',
                                }}
                            />
                        )}
                        <div style={{
                            marginTop: 12,
                            padding: 12,
                            background: 'rgba(23,125,220,0.08)',
                            borderRadius: 8,
                            border: '1px solid rgba(23,125,220,0.2)',
                        }}
                        >
                            <InfoCircleOutlined style={{ color: '#177DDC', marginRight: 8 }} />
                            <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                                {isNested
                                    ? `Ejemplo: para las claves "${firstKey}" y "${firstSubVal}", el valor sería ${exampleVal}.`
                                    : `Ejemplo: para la clave "${firstKey}", el valor sería ${exampleVal}.`}
                            </Text>
                        </div>
                    </div>
                );
            } catch {
                return <Text code copyable>{strVal}</Text>;
            }
        }

        if (vType === 'reference') {
            const fieldMatch = strVal.match(/\{\{booking\.(.+)\}\}/);
            const fieldName = fieldMatch ? fieldMatch[1] : strVal;
            return (
                <div style={{ padding: '8px 0' }}>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 14, display: 'block', marginBottom: 12,
                        }}
                    >
                        Esta variable toma directamente el valor del campo
                        {' '}
                        <strong>{fieldName}</strong>
                        {' '}
                        del pedido. No se evalúa ninguna condición, simplemente se reemplaza por el valor del campo.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                    >
                        <Tag color="cyan" style={{ margin: 0, fontSize: 14, padding: '4px 12px' }}>
                            {fieldName}
                        </Tag>
                        <Text
                            code
                            copyable
                            style={{ fontSize: 13, color: '#8c8c8c' }}
                        >
                            {strVal}
                        </Text>
                    </div>
                    <div style={{
                        marginTop: 12,
                        padding: 12,
                        background: 'rgba(19, 194, 194, 0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(19, 194, 194, 0.2)',
                    }}
                    >
                        <InfoCircleOutlined style={{ color: '#13c2c2', marginRight: 8 }} />
                        <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                            Al calcular, esta variable se sustituye por el valor actual del campo en el pedido.
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'conditional') {
            return (
                <div style={{ padding: '8px 0' }}>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 14, display: 'block', marginBottom: 12,
                        }}
                    >
                        Esta variable evalúa una
                        {' '}
                        <strong>condición</strong>
                        {' '}
                        basada en datos del pedido. Su valor cambia según se cumpla o no la condición.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                    }}
                    >
                        <Text
                            code
                            copyable
                            style={{
                                fontSize: 13,
                                wordBreak: 'break-all',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {strVal}
                        </Text>
                    </div>
                    <div style={{
                        marginTop: 12,
                        padding: 12,
                        background: 'rgba(23,125,220,0.08)',
                        borderRadius: 8,
                        border: '1px solid rgba(23,125,220,0.2)',
                    }}
                    >
                        <InfoCircleOutlined style={{ color: '#177DDC', marginRight: 8 }} />
                        <Text style={{ fontSize: 13, color: '#8c8c8c' }}>
                            El valor de esta variable depende de los datos del pedido al momento de calcular.
                        </Text>
                    </div>
                </div>
            );
        }

        if (vType === 'expression') {
            return (
                <div style={{ padding: '8px 0' }}>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 14, display: 'block', marginBottom: 12,
                        }}
                    >
                        Esta variable contiene una
                        {' '}
                        <strong>expresión</strong>
                        {' '}
                        que se evalúa dinámicamente usando datos del pedido.
                    </Text>
                    <div style={{
                        background: '#141414',
                        border: '1px solid #303030',
                        borderRadius: 8,
                        padding: 16,
                    }}
                    >
                        <Text
                            code
                            copyable
                            style={{
                                fontSize: 13,
                                wordBreak: 'break-all',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {strVal}
                        </Text>
                    </div>
                </div>
            );
        }

        // text / default
        return (
            <div style={{ padding: '8px 0' }}>
                <div style={{
                    background: '#141414',
                    border: '1px solid #303030',
                    borderRadius: 8,
                    padding: 20,
                }}
                >
                    <Text
                        copyable
                        style={{ fontSize: 14, color: '#87d068' }}
                    >
                        {strVal}
                    </Text>
                </div>
            </div>
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name: string) => (
                <Text strong style={{ fontSize: 13 }}>{name}</Text>
            ),
        },
        {
            title: 'Origin',
            dataIndex: 'origin',
            key: 'origin',
            width: 100,
            render: (origin: string) => (
                <Tag color={origin === 'BOOKING' ? 'cyan' : 'purple'}>
                    {origin}
                </Tag>
            ),
        },
        {
            title: 'Scope',
            key: 'scope',
            width: 100,
            render: (_: any, record: VariableItem) => (
                <Tag color={record.planID ? 'default' : 'green'}>
                    {record.planID ? 'Plan' : 'Global'}
                </Tag>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'value',
            key: 'tipo',
            width: 160,
            render: (value: any) => {
                const vType = detectVarType(value);
                const config = VAR_TYPE_CONFIG[vType];
                return <Tag color={config.color}>{config.label}</Tag>;
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (date: string) => moment(date).format('MMM DD, YYYY'),
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: any, record: VariableItem) => (
                <Tooltip title="Editar">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(record);
                        }}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <div>
            <div className={styles.searchBar} id="fev2-variables-search">
                <Input
                    placeholder="Search variables..."
                    prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                    style={{ width: 300 }}
                    allowClear
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                />
                <Select
                    placeholder="Origin"
                    style={{ width: 140 }}
                    allowClear
                    options={VARIABLE_ORIGIN_OPTIONS}
                    value={originFilter}
                    onChange={(val) => {
                        setOriginFilter(val);
                        setPaginator({ ...paginator, page: 1 });
                    }}
                />
                <Select
                    placeholder="Scope"
                    style={{ width: 140 }}
                    allowClear
                    options={[
                        { label: 'Global', value: 'global' },
                        { label: 'Plan-specific', value: 'plan' },
                    ]}
                    value={scopeFilter}
                    onChange={setScopeFilter}
                />
                <Space style={{ marginLeft: 'auto' }}>
                    <Tooltip title="Export CSV">
                        <Button icon={<ExportOutlined />} onClick={handleExport} />
                    </Tooltip>
                    <Tooltip title="Reload">
                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Agregar
                    </Button>
                </Space>
            </div>

            <div className={styles.tableWrapper}>
                <Table
                    dataSource={filteredVariables}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    expandable={{
                        expandedRowRender: renderExpandedVariable,
                        expandedRowKeys: expandedVar ? [expandedVar] : [],
                        onExpand: (expanded, record) => {
                            setExpandedVar(expanded ? record._id : null);
                        },
                    }}
                    pagination={{
                        current: paginator.page,
                        pageSize: paginator.limit,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        onChange: (page, pageSize) => setPaginator({ page, limit: pageSize || 10 }),
                    }}
                />
            </div>

            <VariableEditorModal
                visible={editorVisible}
                variable={editingVariable}
                onClose={handleEditorClose}
            />
        </div>
    );
};

export { VariablesTab };
